import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import TreadLiveMapV2 from '~components/Maps/v2/TreadLiveMapV2';
import useActiveHeartbeat from '~hooks/useActiveHeartbeat/useActiveHeartbeat';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Load, LoadCycle } from '~hooks/useLoad/models';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { Order } from '~hooks/useOrders/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { APPBAR_HEIGHT_IN_PX } from '../Dispatch/components/drivers/constants';
import TreadLiveSideBar from './TreadLiveSideBar';

/*
 * TreadLivePage
 *
 * The TreadLivePage component is responsible for rendering the live map view using only location data pulled from Tread APIs.
 */
const TreadLivePage = () => {
  const { jobId } = useParams();
  const theme = useTheme();
  const { liveMapStoreNew, userStore } = useStores();

  // Render-phase (not an effect) so the restored dates are already in the store
  // when OrderList's fetch effect takes its dependency snapshot — an effect-
  // based restore would fetch once with the today-default and immediately
  // refetch with the stored range. Ref-guarded: runs once per session, on mount.
  const hasRestoredDateFiltersRef = useRef(false);
  if (!hasRestoredDateFiltersRef.current) {
    hasRestoredDateFiltersRef.current = true;
    liveMapStoreNew.restoreDateFilters();
  }

  const [selectedJob, setSelectedJob] = useState<Job>();
  const [selectedOrder, setSelectedOrder] = useState<Order>();
  const [loads, setLoads] = useState<Load[]>([]);
  const [loadCycles, setLoadCycles] = useState<LoadCycle[]>([]);
  const [isTimelineVisible, setIsTimelineVisible] = useState<boolean>(false);
  const [geofenceRecenterPosition, setGeofenceRecenterPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [geofenceDrawingCoordinates, setGeofenceDrawingCoordinates] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [geofenceDrawingMode, setGeofenceDrawingMode] = useState(false);
  const [geofenceCircleRadius, setGeofenceCircleRadius] = useState<number | null>(null);
  const [isMapPickMode, setIsMapPickMode] = useState(false);
  const [pickedMapCoordinates, setPickedMapCoordinates] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [geofenceRoutePings, setGeofenceRoutePings] = useState<
    LocationHistoryMinimal[] | null
  >(null);
  const [geofenceRouteJob, setGeofenceRouteJob] = useState<Job | undefined>(undefined);
  const [isLoadingGeofenceRoutePings, setIsLoadingGeofenceRoutePings] = useState(false);

  const handleGeofenceViewRoute = (pings: LocationHistoryMinimal[], job?: Job) => {
    setGeofenceRoutePings(pings);
    setGeofenceRouteJob(job);
    // Clear job route timeline to prevent both timelines from stacking
    setIsTimelineVisible(false);
  };

  const handleMapLocationPick = (coordinates: { lat: number; lng: number }) => {
    setPickedMapCoordinates(coordinates);
    setGeofenceRecenterPosition(coordinates);
    setIsMapPickMode(false);
  };

  const handlePickLocationModeToggle = () => {
    setIsMapPickMode(!isMapPickMode);
  };

  const handlePickedMapCoordinatesProcessed = () => {
    setPickedMapCoordinates(null);
  };

  const handleGeofenceDrawingCoordinatesChange = (
    coordinates: { lat: number; lng: number }[],
    isPolygonMode: boolean,
  ) => {
    setGeofenceDrawingCoordinates(coordinates);
    setGeofenceDrawingMode(isPolygonMode);
  };

  const handleGeofencePolygonEdit = (newCoordinates: { lat: number; lng: number }[]) => {
    setGeofenceDrawingCoordinates(newCoordinates);
    if (newCoordinates.length >= 1) {
      const lat = newCoordinates.reduce((s, c) => s + c.lat, 0) / newCoordinates.length;
      const lng = newCoordinates.reduce((s, c) => s + c.lng, 0) / newCoordinates.length;
      setGeofenceRecenterPosition({ lat, lng });
    }
  };

  const handleGeofenceCircleEdit = (
    center: { lat: number; lng: number },
    radius: number,
  ) => {
    setGeofenceRecenterPosition(center);
    setGeofenceCircleRadius(Math.round(radius));
  };

  const handleGeofenceLocationDragEnd = (position: { lat: number; lng: number }) => {
    setGeofenceRecenterPosition(position);
    setGeofenceDrawingCoordinates((prev) => {
      if (prev.length < 3) return prev;
      const lat = prev.reduce((s, c) => s + c.lat, 0) / prev.length;
      const lng = prev.reduce((s, c) => s + c.lng, 0) / prev.length;
      const deltaLat = position.lat - lat;
      const deltaLng = position.lng - lng;
      return prev.map((c) => ({
        lat: c.lat + deltaLat,
        lng: c.lng + deltaLng,
      }));
    });
  };

  const handleGeofenceReportsTabLeave = () => {
    setGeofenceRecenterPosition(null);
    setGeofenceDrawingCoordinates([]);
    setGeofenceDrawingMode(false);
    setGeofenceCircleRadius(null);
  };

  const companyId = getEffectiveUserCompanyId(userStore);
  useActiveHeartbeat({ company_id: companyId, page: 'live' });

  const viewRouteForSelectedJob = (selectedJob: Job) => {
    if (selectedJob?.id) {
      setIsTimelineVisible(true);
      // Clear geofence route to prevent both timelines from stacking
      setGeofenceRoutePings(null);
      setGeofenceRouteJob(undefined);
    }
  };

  const handleBackOnClick = () => {
    setSelectedJob(undefined);
    setSelectedOrder(undefined);
    // Reset timeline visibility so truck layers are shown again
    // Without this, isTimelineVisible stays true and trucks remain hidden
    // (see TreadLiveMapV2.tsx line ~1397 where truck layers are conditionally added)
    setIsTimelineVisible(false);
  };

  const { getJobLoads, getJobLoadCycles, getJobById } = useJob();

  useEffect(() => {
    if (jobId) {
      getJobById(jobId, companyId)
        .then((job) => {
          if (job) {
            setSelectedJob(job);
            viewRouteForSelectedJob(job);
          }
        })
        .catch((error) => {
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_fetch_individual_job'),
          );
        });
    }
  }, [jobId]);

  useEffect(() => {
    if (selectedJob?.id) {
      getJobLoads(selectedJob.id).then(setLoads);
      getJobLoadCycles(selectedJob.id).then(setLoadCycles);
    } else {
      setLoads([]);
      setLoadCycles([]);
    }
  }, [selectedJob?.id]);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        height: `calc(100vh - ${APPBAR_HEIGHT_IN_PX}px - var(--tread-agent-dock-height, 0px))`,
        mx: -5,
        overflow: 'hidden',
      }}
    >
      {/* Top bar with filters */}
      <Box
        sx={{
          width: liveMapStoreNew.isSidebarCollapsed ? '50px' : '500px',
          [theme.breakpoints.down('lg')]: {
            width: liveMapStoreNew.isSidebarCollapsed ? '50px' : '450px',
          },
          height: `calc(100vh - ${APPBAR_HEIGHT_IN_PX}px - var(--tread-agent-dock-height, 0px))`,
          transition: 'width 0.3s',
        }}
      >
        <TreadLiveSideBar
          onSelectJob={(job) => {
            setSelectedJob(job);
            // Don't clear selectedOrder - this keeps the order card expanded when selecting a job
          }}
          onSelectOrder={(order: Order | undefined) => {
            setSelectedOrder(order);
            setSelectedJob(undefined);
          }}
          onViewJobRoute={(job) => {
            setSelectedJob(job);
            // Don't clear selectedOrder - this keeps the order card expanded when viewing a job route
            viewRouteForSelectedJob(job);
          }}
          selectedJob={selectedJob}
          selectedOrder={selectedOrder}
          isSidebarCollapsed={liveMapStoreNew.isSidebarCollapsed}
          onGeofenceLocationChange={setGeofenceRecenterPosition}
          onGeofenceDrawingCoordinatesChange={handleGeofenceDrawingCoordinatesChange}
          onGeofenceCircleRadiusChange={setGeofenceCircleRadius}
          geofenceRecenterPosition={geofenceRecenterPosition}
          geofenceCircleRadius={geofenceCircleRadius}
          geofenceDrawingCoordinates={geofenceDrawingCoordinates}
          isMapPickMode={isMapPickMode}
          onPickLocationModeToggle={handlePickLocationModeToggle}
          pickedMapCoordinates={pickedMapCoordinates}
          onPickedMapCoordinatesProcessed={handlePickedMapCoordinatesProcessed}
          onGeofenceViewRoute={handleGeofenceViewRoute}
          onGeofenceViewRouteLoadingChange={setIsLoadingGeofenceRoutePings}
          onGeofenceReportsTabLeave={handleGeofenceReportsTabLeave}
        />
      </Box>
      {/* Live Map */}
      <Box
        className={'live-map'}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          flexGrow: 1,
          flexShrink: 1,
          height: '100%',
          width: '100%',
          paddingTop: 0,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
          }}
        >
          <TreadLiveMapV2
            job={selectedJob}
            order={selectedOrder}
            onBackClick={handleBackOnClick}
            isTimelineVisible={isTimelineVisible}
            loads={loads}
            loadCycles={loadCycles}
            showHoveredLoadStats
            onViewJobRoute={(job) => {
              setSelectedJob(job);
              setSelectedOrder(undefined);
              viewRouteForSelectedJob(job);
            }}
            geofenceRecenterPosition={geofenceRecenterPosition}
            geofenceDrawingMode={geofenceDrawingMode}
            geofenceDrawingCoordinates={geofenceDrawingCoordinates}
            onGeofencePolygonEdit={handleGeofencePolygonEdit}
            geofenceCircleRadius={geofenceCircleRadius}
            onGeofenceCircleEdit={handleGeofenceCircleEdit}
            onGeofenceLocationDragEnd={handleGeofenceLocationDragEnd}
            isMapPickMode={isMapPickMode}
            onMapLocationPick={handleMapLocationPick}
            geofenceRoutePings={geofenceRoutePings}
            geofenceRouteJob={geofenceRouteJob}
            isLoadingGeofenceRoutePings={isLoadingGeofenceRoutePings}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default observer(TreadLivePage);
